<script setup lang="ts">
  import { type ButtonSize } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import { HeaderAccount, type AccountMenuKey, type AccountUser } from '@/features/account';
  import { useViewport, Breakpoint } from '@/shared/composables';
  import SkolkovoLogo from '@/shared/ui/logos/SkolkovoLogo.vue';
  import TravelataLogo from '@/shared/ui/logos/TravelataLogo.vue';
  import HeaderNav from './HeaderNav.vue';
  import { type NavLink } from '../config/navLinks';

  export interface SiteHeaderProps {
    user?: AccountUser | null;
    accountUrl?: string;
    visibleLogos?: boolean;
    visibleSkolkovo?: boolean;
    shouldAdaptive?: boolean;
    buttonBordered?: boolean;
    accountButtonSize?: `${ButtonSize}`;
    accountMenuKeys?: AccountMenuKey[];
    primary?: NavLink[];
    more?: NavLink[];
  }

  const props = withDefaults(defineProps<SiteHeaderProps>(), {
    user: null,
    accountUrl: '',
    visibleLogos: true,
    visibleSkolkovo: undefined,
    shouldAdaptive: true,
    buttonBordered: true,
    accountButtonSize: undefined,
    accountMenuKeys: undefined,
    primary: undefined,
    more: undefined,
  });

  interface SiteHeaderEmits {
    (e: 'login'): void;
    (e: 'logout'): void;
  }

  const emit = defineEmits<SiteHeaderEmits>();

  const { isSmaller } = useViewport();

  const isBreakpointActive = isSmaller(Breakpoint.L);

  const isAdaptive = computed(() => isBreakpointActive.value && props.shouldAdaptive);

  // Бейдж Sk отделён от логотипа: витрине нужен логотип партнёра, но не нужен Sk. Без явного
  // visibleSkolkovo следует за visibleLogos, чтобы главная сохранила прежнее поведение.
  const showSkolkovo = computed(() => props.visibleSkolkovo ?? props.visibleLogos);

  const onLogin = (): void => {
    emit('login');
  };

  const onLogout = (): void => {
    emit('logout');
  };
</script>

<template>
  <header
    :class="[
      'site-header',
      {
        'site-header_adaptive': shouldAdaptive,
      },
    ]"
    data-testid="site-header"
  >
    <div class="site-header__bar">
      <div class="site-header__left">
        <!-- Витрина кладёт сюда HeaderLogo; без слота остаётся логотип Travelata. -->
        <slot name="logo">
          <TravelataLogo v-if="visibleLogos" />
        </slot>
        <template v-if="!isAdaptive">
          <slot name="nav"></slot>
          <HeaderNav
            v-if="!$slots.nav"
            :primary="primary"
            :more="more"
          />
        </template>
      </div>

      <div class="site-header__right">
        <SkolkovoLogo v-if="showSkolkovo" />
        <div class="site-header__action">
          <slot name="action"></slot>
          <HeaderAccount
            :user="user"
            :account-url="accountUrl"
            :bordered="buttonBordered"
            :button-size="accountButtonSize"
            :menu-keys="accountMenuKeys"
            @login="onLogin"
            @logout="onLogout"
          />
        </div>
      </div>
      <div
        v-if="isAdaptive"
        class="site-header__bottom"
      >
        <slot name="nav"></slot>
        <HeaderNav
          v-if="!$slots.nav"
          :primary="primary"
          :more="more"
        />
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
  .site-header {
    $root: &;

    @include header-background;

    box-sizing: border-box;
    display: flex;
    justify-content: center;
    padding: var(--spacing-s) var(--spacing-l) 0 var(--spacing-l);
    width: 100%;

    &__action {
      align-items: center;
      display: flex;
      gap: var(--spacing-s);
      justify-content: center;
    }

    &__bar {
      align-items: center;
      display: flex;
      gap: var(--spacing-l);
      justify-content: space-between;
      margin: 0 auto;
      max-width: $max-width-xl;

      // Относительно бара абсолютно центрируется знак Travelata в режиме logoType=1.
      position: relative;
      width: 100%;
    }

    &__left {
      align-items: center;
      display: flex;
      gap: var(--spacing-xl);
    }

    &__right {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxl);
    }

    &_adaptive {
      @media (max-width: $breakpoint-l) {
        #{$root}__bar {
          align-items: center;
          display: grid;
          gap: var(--spacing-m) var(--spacing-l);
          grid-template-columns: 1fr auto;
          grid-template-rows: auto auto;
        }

        #{$root}__left {
          grid-column: 1 / 2;
          grid-row: 1 / 2;
        }
        #{$root}__right {
          grid-column: 2 / 3;
          grid-row: 1 / 2;
          justify-self: end;
        }
        #{$root}__bottom {
          align-items: center;
          display: flex;
          grid-column: 1 / -1;
          grid-row: 2 / 3;
          justify-content: space-between;
        }
      }
    }

    @include responsive-max-width('&__bar');
  }
</style>
